import React, { useState, useMemo, useEffect, useRef } from "react";
import { AiOutlineClose } from "react-icons/ai";
import { FaCode } from "react-icons/fa";
import { BsFiletypeCsv } from "react-icons/bs";
import { PiMicrosoftExcelLogoLight } from "react-icons/pi";
import { FaFilePdf } from "react-icons/fa";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { dracula } from "react-syntax-highlighter/dist/esm/styles/prism";
import Loader from "../../Loader/Loader";
import DataTable, { DataTableHandle } from "./DataGrid";
import { toast } from "react-toastify";
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import * as XLSX from "xlsx";
import CodeExecuter from "../CodeExecuter/CodeExecuter";
// import Roboto from "../../../Assets/fonts/Roboto-Medium.ttf";
import { Chart } from "chart.js";
import {
  Bar,
  Line,
  Pie,
  Doughnut,
  Radar,
  PolarArea,
  Bubble,
  Scatter,
} from "react-chartjs-2";
pdfMake.vfs = pdfFonts.vfs;

// pdfMake.vfs = {
//   ...pdfFonts.vfs,
//   "Roboto-Medium.ttf": Roboto,
// };

interface PreviewProps {
  showPreview: boolean;
  setShowPreview: (show: boolean) => void;
  onPreviewWidthChange?: (width: number) => void;

  /* — live-session versions — */
  versions: any[];
  activeVersionId: string | null;
  setActive: (id: string | null) => void;
  setActiveVersionNumber: (version: number | null) => void;
  activeVersionNumber: number | null;

  /* — live-session streaming data — */
  previewContent: string;
  sqlCode: string;
  graphCode: string;
  liveMode: boolean;

  selectedVersionName: string;
  selectedRoomId: number;
  setVerionId: any;

  /* — SQL modal — */
  showCodeModal: boolean;
  setShowCodeModal: (show: boolean) => void;

  /* — "Download Preview" drop-down — */
  showDownloadMenu: boolean;
  setShowDownloadMenu: (show: boolean) => void;

  /* — historic rows returned from the backend — */
  historyData: any[];

  versionToSend: any;

  /* kept from old component, still passed by parent: */
  selected: any; // current live row object (if parent still relies on it)
  loading: boolean;
}

const Preview: React.FC<PreviewProps> = ({
  showPreview,
  setShowPreview,
  onPreviewWidthChange,

  versions,
  activeVersionId,
  setActive,
  activeVersionNumber,
  setActiveVersionNumber,

  previewContent,
  sqlCode,
  liveMode,

  showCodeModal,
  setShowCodeModal,

  showDownloadMenu,
  setShowDownloadMenu,
  setVerionId,

  historyData,
  versionToSend,
  selected,
  selectedVersionName,
  selectedRoomId,
  graphCode,
  loading,
}) => {
  const [previewWidth, setPreviewWidth] = useState(900);
  const resizerRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const graphContainerRef = useRef<HTMLDivElement>(null);
  const tableRef = useRef<DataTableHandle | null>(null);

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    const startX = e.clientX;

    const handleMouseMove = (moveEvent: MouseEvent) => {
      const newWidth = previewWidth + (moveEvent.clientX - startX);
      setPreviewWidth(newWidth);
      onPreviewWidthChange?.(newWidth);
    };

    const handleMouseUp = () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  const graphScope = useMemo(
    () => ({
      Chart,
      Bar,
      Line,
      Pie,
      Doughnut,
      Radar,
      PolarArea,
      Bubble,
      Scatter,
    }),
    []
  );

  //  Build ONE unified list of "version" objects

  // const allVersions = useMemo(() => {
  //   const liveRows = versions.map((v) => ({
  //     id: v.id,
  //     label: `V${v.version}`,
  //     sqlCode: v.sqlCode,
  //     graphCode: v.graphCode,
  //     previewContent: v.previewContent,
  //     versionNumber: v.version,
  //     isLive: true,
  //   }));

  //   const historicRows =
  //     historyData?.flatMap((item) =>
  //       item?.chat_settings
  //         ? [
  //             {
  //               id: item.chat_settings.version_name,
  //               label: item.chat_settings.version_name,
  //               sqlCode: item.chat_settings.sql_query,
  //               graphCode: item.chat_settings.graph_code,
  //               previewContent: item.chat_settings.sql_query_result,
  //               versionNumber: item.chat_settings.version_name,
  //               isLive: false,
  //             },
  //           ]
  //         : []
  //     ) || [];

  //   return [...liveRows, ...historicRows];
  // }, [versions, historyData]);

  const allVersions = useMemo(() => versions, [versions]);

  // Currently selected version

  const [currentVersionId, setCurrentVersionId] = useState<string>(
    allVersions.length ? allVersions[0].id : null
  );

  setVerionId(currentVersionId);

  // Effect to update currentVersionId based on selectedVersionName
  useEffect(() => {
    if (selectedVersionName) {
      const foundVersion = allVersions.find(
        (v) => v.label === selectedVersionName
      );
      if (foundVersion) {
        setCurrentVersionId(foundVersion.id);
        setVerionId(foundVersion.id);
      }
    }
  }, [selectedVersionName, allVersions, setVerionId]);

  // Effect to set currentVersionId when a new version is created
  useEffect(() => {
    if (activeVersionId) {
      setCurrentVersionId(activeVersionId);
    }
  }, [activeVersionId]);

  useEffect(() => {
    setVerionId(currentVersionId);
  }, [currentVersionId, setVerionId]);

  const activeVersion = allVersions.find((v) => v.id === currentVersionId);

  /** SQL to display in the modal */
  const sqlForModal =
    activeVersion?.isLive && liveMode ? sqlCode : activeVersion?.sqlCode || "";

  /** Graph to display in the Chart */
  let codeForGraph = activeVersion?.graphCode || "";
  if (
    activeVersion?.isLive &&
    liveMode &&
    activeVersion.id === activeVersionId
  ) {
    codeForGraph = graphCode;
    // console.log(
    //   `[Preview] Using liveMode graphCode for version`,
    //   activeVersion?.versionNumber,
    //   "id:",
    //   activeVersion?.id,
    //   "graphCode:",
    //   codeForGraph
    // );
  } else if (activeVersion?.graphCode) {
    codeForGraph = activeVersion.graphCode;
    // console.log(
    //   `[Preview] Using stored graphCode for version`,
    //   activeVersion?.versionNumber,
    //   "id:",
    //   activeVersion?.id,
    //   "graphCode:",
    //   codeForGraph
    // );
  } else {
    codeForGraph = "";
    // console.log(
    //   `[Preview] No graphCode for version`,
    //   activeVersion?.versionNumber,
    //   "id:",
    //   activeVersion?.id,
    //   "graphCode is empty, will not render graph."
    // );
  }

  // Effect to handle scrolling when version or history changes
  useEffect(() => {
    // Reset the scroll behavior by forcing a re-render
    if (graphContainerRef.current) {
      graphContainerRef.current.scrollIntoView({
        behavior: "smooth",
        block: "end",
      });
    }
  }, [historyData]);

  // Effect to handle scrolling when graph code changes
  useEffect(() => {
    const hasGraphCode = Boolean(codeForGraph && codeForGraph.trim() !== "");

    if (hasGraphCode && graphContainerRef.current) {
      setTimeout(() => {
        if (graphContainerRef.current) {
          graphContainerRef.current.scrollIntoView({
            behavior: "smooth",
            block: "end",
          });
        }
      }, 100);
    }
  }, [codeForGraph]);

  const codeExecuterElement = useMemo(
    () => <CodeExecuter code={codeForGraph} scope={graphScope} />,
    [codeForGraph, graphScope]
  );

  /** Preview markdown to render */
  const previewToRender =
    activeVersion?.isLive && liveMode
      ? previewContent
      : activeVersion?.previewContent || "";

  // Render nothing if Preview is hidden

  if (!showPreview) return null;

  const handleDownloadPDF = () => {
    const tableData = tableRef.current?.getExportData();
    if (!tableData) {
      toast.error("No table data found to download");
      return;
    }
    if (tableData.headers && tableData.headers.length > 3) {
      toast.info(
        "This data is large; it's recommended to download it as a CSV or an Excel file"
      );
      return;
    }
    const formattedRows = tableData.rows.map((row) =>
      row.map((cell) => {
        if (/^\s*\d+\.?\d*e\+\d+\s*$/.test(cell)) {
          return Number(cell).toLocaleString();
        }
        return cell;
      })
    );
    const docDefinition = {
      content: [
        { text: "Data Summary", style: "header", margin: [0, 0, 0, 10] },
        {
          table: {
            headerRows: 1,
            widths: Array(tableData.headers.length).fill("*"),
            body: [tableData.headers, ...formattedRows],
          },
        },
      ],
      styles: {
        header: {
          fontSize: 10,
          bold: true,
          alignment: "center",
          margin: [0, 0, 0, 10],
        },
      },
    };
    pdfMake.createPdf(docDefinition).download("data_summary.pdf");
  };

  const handleDownloadCSV = () => {
    const tableData = tableRef.current?.getExportData();
    if (!tableData) {
      toast.error("No table data found to download");
      return;
    }
    try {
      const formattedRows = tableData.rows.map((row) =>
        row.map((cell) => {
          if (/^\s*\d+\.?\d*e\+\d+\s*$/.test(cell)) {
            return Number(cell).toLocaleString();
          }
          return cell;
        })
      );
      const csvContent = [
        tableData.headers.join(";"),
        ...formattedRows.map((row) => row.join(";")),
      ].join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute("download", "data_summary.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading CSV:", error);
      toast.error("Failed to download CSV");
    }
  };

  const handleDownloadExcel = () => {
    const tableData = tableRef.current?.getExportData();
    if (!tableData) {
      toast.error("No table data found to download");
      return;
    }
    try {
      const formattedRows = tableData.rows.map((row) =>
        row.map((cell) => {
          if (/^\s*\d+\.?\d*e\+\d+\s*$/.test(cell)) {
            return Number(cell);
          }
          return cell;
        })
      );
      const ws = XLSX.utils.aoa_to_sheet([tableData.headers, ...formattedRows]);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Data Summary");
      XLSX.writeFile(wb, "data_summary.xlsx");
    } catch (error) {
      console.error("Error downloading Excel:", error);
      toast.error("Failed to download Excel file");
    }
  };

  // no markdown parsing; we use the table ref export

  return (
    <div className="flex">
      <div
        ref={resizerRef}
        onMouseDown={handleMouseDown}
        className="cursor-col-resize w-1 bg-gray-300"
        style={{ height: "100%", userSelect: "none" }}
      />
      <div
        className="w-[60%] bg-white border-l border-gray-200 p-4 overflow-y-auto relative"
        style={{ width: `${previewWidth}px` }} // Set width dynamically
      >
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">Preview</h2>
          <button
            onClick={() => setShowPreview(false)}
            className="text-gray-500 hover:text-gray-700"
          >
            <AiOutlineClose size={20} />
          </button>
        </div>

        <div className="flex items-center justify-between mb-4 gap-2">
          <div className="flex flex-row items-center gap-1">
            {allVersions.length > 0 && (
              <div className="flex flex-row gap-1 items-center">
                <p>Version:</p>
                <select
                  value={currentVersionId || ""}
                  onChange={(e) => {
                    const id = e.target.value;
                    setCurrentVersionId(id);

                    const picked = allVersions.find((v) => v.id === id);
                    if (picked) {
                      setActive(id);
                      setActiveVersionNumber(picked.version);
                    }
                  }}
                  className="px-3 py-1 rounded text-sm bg-gray-200 outline-none"
                >
                  <option value="" disabled>
                    Version
                  </option>
                  {allVersions.map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.label}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <button
              className="px-4 py-2 bg-gray-400 text-white rounded hover:bg-gray-500 transition duration-300"
              onClick={() => setShowCodeModal(true)}
            >
              <FaCode />
            </button>
          </div>

          <div className="flex gap-2">
            <div className="relative download-dropdown">
              <button
                onClick={() => {
                  setShowDownloadMenu(true);
                }}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
              >
                <span>Download Preview</span>
              </button>

              {showDownloadMenu && (
                <div
                  className="absolute mt-2 w-40 bg-white rounded-md shadow-lg z-50"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="py-1">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setShowDownloadMenu(false);
                        handleDownloadCSV();
                      }}
                      className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      disabled={!tableRef.current}
                    >
                      <BsFiletypeCsv size={25} className="mr-2 text-blue-600" />
                      CSV
                    </button>

                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setShowDownloadMenu(false);
                        handleDownloadExcel();
                      }}
                      className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      disabled={!tableRef.current}
                    >
                      <PiMicrosoftExcelLogoLight
                        size={25}
                        className="mr-2 text-green-600"
                      />
                      Excel
                    </button>

                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setShowDownloadMenu(false);
                        handleDownloadPDF();
                      }}
                      className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      disabled={!tableRef.current}
                    >
                      <FaFilePdf size={25} className="mr-2 text-red-600" />
                      PDF
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {previewToRender.length ? (
          <>
            <div
              ref={containerRef}
              className="overflow-x-auto overflow-y-auto mb-6"
              style={{ maxHeight: "calc(100vh - 180px)" }}
            >
              <div className="markdown-paragraph w-full">
                <DataTable ref={tableRef} rawData={previewToRender} />
              </div>
              {codeForGraph && codeForGraph.trim() !== "" && (
                <div ref={graphContainerRef}>{codeExecuterElement}</div>
              )}
            </div>
          </>
        ) : !loading ? (
          <div className="flex flex-row gap-2 items-center text-red-500">
            No data to display
          </div>
        ) : (
          <div className="flex flex-row gap-2 items-center">
            Processing <Loader />
          </div>
        )}

        {showCodeModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
            <div className="bg-white rounded shadow-lg p-6 min-w-[550px] max-w-[90vw] relative">
              <button
                className="absolute top-2 right-2 text-gray-500 hover:text-gray-700"
                onClick={() => setShowCodeModal(false)}
              >
                <AiOutlineClose size={22} />
              </button>
              <h3 className="text-lg font-bold mb-4">SQL Query</h3>

              <div className="max-h-[60vh] overflow-auto">
                <SyntaxHighlighter language="sql" style={dracula}>
                  {sqlForModal || sqlCode}
                </SyntaxHighlighter>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Preview;
